import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { FormikProvider, useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Modal from "../Modal";
import { Button } from "../Button";
import BackButton from "../BackButton";
import FormikInput from "../FormikInput";
import VerifyEmail from "../VerifyEmail";
import CreatePassword from "../../pages/CreatePassword";

import { useService } from "@/base/hooks/useService";
import { StorageService } from "@services/StorageService";

import {
  forgotPasswordSendEmail,
  forgotPasswordReset,
  toggleForgotPasswordModal,
} from "@redux/forgotPassword/actions";
import { toggleSignInModal } from "@redux/signIn/actions";
import { verificationPasswordCode } from "@redux/verification/actions";

import { ErrorCodes } from "@const/errors";
import { localstorageKeys } from "@const/store";
import { initialValues, validationSchema } from "./form";
import { MAX_EMAIL_LENGTH } from "@validation/lengthConstants";

import styles from "./index.module.scss";

const ForgotPasswordModal = () => {
  const { t } = useTranslation("reset-pwd");
  const { t: t1 } = useTranslation("buttons");
  const dispatch = useDispatch();
  const storageService = useService(StorageService);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const verifyEmailRef = useRef(null);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(true);
  const [createPasswordScreen, setCreatePasswordScreen] = useState(false);

  const { error, isLoading, isSuccess, isForgotPasswordModalVisible } =
    useSelector(({ forgotPasswordReducer }) => forgotPasswordReducer);

  const handleSubmit = async (values) => {
    if (values.email === email) {
      setForgotPasswordStep(false);
    } else {
      setEmail(values.email);
      dispatch(forgotPasswordSendEmail(values));
      storageService.set(localstorageKeys.sentAt, Date.now());
      verifyEmailRef.current?.resetLimit();
      verifyEmailRef.current?.resetSeconds();
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const closeModal = () => {
    dispatch(toggleForgotPasswordModal(false));
    // setTimeout is used to prevent flickering
    setTimeout(() => {
      setForgotPasswordStep(true);
      setCreatePasswordScreen(false);
      setEmail("");
      formik.resetForm();
    }, 500);
  };

  const onBackClick = () => {
    setForgotPasswordStep(true);
  };

  useEffect(() => {
    if (!isSuccess) return;

    storageService.set(localstorageKeys.sentAt, Date.now());
    setForgotPasswordStep(false);
  }, [isSuccess]);

  useEffect(() => {
    if (isForgotPasswordModalVisible) return;

    dispatch(forgotPasswordReset());
  }, [isForgotPasswordModalVisible]);

  useEffect(() => {
    if (error?.code === ErrorCodes.RESEND_LIMIT_EXCEED && forgotPasswordStep) {
      setForgotPasswordStep(false);
    }
  }, [error]);

  const successCallback = (verificationCode) => {
    dispatch(
      forgotPasswordReset({
        isForgotPasswordModalVisible: true,
      }),
    );
    setCreatePasswordScreen(true);
    setCode(verificationCode);
  };

  const goBackToLogin = () => {
    closeModal();

    dispatch(toggleSignInModal(true));
  };

  const sendFunction = (code) => {
    dispatch(verificationPasswordCode({ email, code }));
  };

  const resendFunction = () => {
    dispatch(forgotPasswordSendEmail({ email }));
  };

  return (
    <Modal
      centered
      unmountOnClose
      showCancel={false}
      showConfirm={false}
      onClose={closeModal}
      footerClassName="d-none"
      bodyClassName={styles.body}
      isOpen={isForgotPasswordModalVisible}
    >
      {!createPasswordScreen ? (
        <>
          <div
            style={{
              display: forgotPasswordStep ? "flex" : "none",
            }}
            className={classNames(
              "container w-100 h-100 flex-column justify-content-center",
              styles.content,
            )}
          >
            <BackButton onClick={goBackToLogin} />
            <h3 className="text-primary h3 text-medium text-center mb-4">
              {t("TITLE")}
            </h3>
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit}>
                <FormikInput
                  maxLength={MAX_EMAIL_LENGTH}
                  label="EMAIL"
                  placeholder="EMAIL"
                  wrapperClassName="mb-4"
                  name="email"
                />
                <Button
                  size="lg"
                  color="primary"
                  className="w-100"
                  type="submit"
                  disabled={
                    isLoading || !formik.isValid || !formik.values.email.length
                  }
                >
                  {t1("SEND")}
                </Button>
              </form>
            </FormikProvider>
          </div>

          <VerifyEmail
            email={email}
            ref={verifyEmailRef}
            onBackClick={onBackClick}
            sendFunction={sendFunction}
            resendFunction={resendFunction}
            isVisible={!forgotPasswordStep}
            successCallback={successCallback}
            title={t("TITLE")}
            subTitle={t("SUB_TITLE")}
          />
        </>
      ) : (
        <CreatePassword code={code} email={email} closeModal={closeModal} />
      )}
    </Modal>
  );
};

export default ForgotPasswordModal;
